/* eslint-disable no-param-reassign */
import { useCallback, useEffect, useRef, useState } from 'react';
import { isEmpty, isEqual, keyBy } from 'lodash';
import {
  AllocationsService,
  ApiService,
  AppliedMethodologySerializer,
  MultiAllocationInputSerializer,
} from 'api';
import { OPM } from 'common/constants/allocations';
import { useResponse } from 'services/hooks';
import {
  currentISODate,
  dbShortDate,
  extractResponseErrorMessage,
  extractServerErrorMessage,
  toString,
  validateWeighting,
} from 'utilities';
import { ColumnWithWeightProps } from 'utilities/validateWeighting/validateWeighting';
import calculateWeightedPerShareValues from './calculateWeightedPerShareValue';
import {
  BasketSecurityOption,
  CalculateEquityValueProps,
  CalculationInputChange,
  ExecuteBacksolveCalculationParams,
  ScenarioDataInput,
  ScenarioDataValidity,
  UpdateScenarioValuesProps,
} from './types';

function getAndAddCell(spreadsheets: any, onCellsChanged: any) {
  return (s: any) => {
    const cellKey = `${s.security.id}-${s.col}`;
    const cell =
      spreadsheets.backsolveSheet.cells[cellKey] ??
      Object.values(spreadsheets.backsolveSheet.cells).find(
        (c: any) => c.securityName === s.security.name && c.y === s.col
      );
    if (cell) {
      const value = s.value ?? 0;
      onCellsChanged(cell, value);
    }
  };
}

function checkEmptyRows(
  newDistribution: any,
  spreadsheets: any,
  onCellsChanged: any,
  data: any,
  isTargetValueExplicitlySet: boolean
) {
  const newRows = newDistribution.map((dist: any) => dist.presentValues);
  if (!isEmpty(newRows)) {
    newRows.forEach((securities: any) => {
      if (!isEmpty(securities)) {
        securities.forEach(getAndAddCell(spreadsheets, onCellsChanged));
      }
    });

    const appliedMethodologies = data.applied_methodologies;
    const totalPresentShareValues = calculateWeightedPerShareValues(newRows, appliedMethodologies);

    data.basket.forEach((b: any, i: number) => {
      const presentValue = totalPresentShareValues.find(
        (tpsv: any) => Number(tpsv.securityId) === Number(b.security.id)
      );
      const cell = spreadsheets.securitySheet.cells[`C${i + 2}`];

      onCellsChanged(cell, presentValue?.value ?? 0);
    });

    // Restore the target this solve ran for so the solver's own output
    // cannot rewrite it -- but only once the user has typed a target.
    const totalsRowNumber = data.basket.length + 2;
    const targetValueCell = spreadsheets.securitySheet.cells[`D${totalsRowNumber}`];

    if (isTargetValueExplicitlySet && targetValueCell && !targetValueCell.readOnly) {
      onCellsChanged(targetValueCell, data.target_value);
    }

    spreadsheets.securitySheet.tableData.securitiesBasket.target_value = data.target_value;
  }
}

function changeIfBacksolveSummarySheet(spreadsheets: any, onCellsChanged: any, equityValue: number) {
  if (spreadsheets.backsolveSummarySheet) {
    onCellsChanged(spreadsheets.backsolveSummarySheet.cells.B2, equityValue);
  }
}

const populateBasket = (currentNonZeroRows: any, securityList: BasketSecurityOption[] | undefined) => {
  const currentBasket: any = [];
  currentNonZeroRows.forEach((row: any) => {
    const security = securityList?.find((s: any) => toString(s.id) === toString(row.security));

    if (security) {
      currentBasket.push({
        security: {
          id: security.id,
          name: security.name,
        },
        shares: Number(row.shares),
        value: Number(row.value),
      });
    }
  });
  return currentBasket;
};

const populateAppliedMethodologies = (
  appliedMethodologies: any,
  maturity: number,
  volatility: number,
  riskFreeRate: number | undefined,
  backsolveDate: string | null
) => {
  const methodologies = appliedMethodologies.map((m: any) => ({
    allocation_method: m.allocation_method,
    cap_table: Number(m.cap_table),
    weight: parseFloat(m.weight),
    maturity: Number(m.allocation_method) === Number(OPM) ? maturity : 0,
    volatility: Number(m.allocation_method) === Number(OPM) ? volatility : 0,
    // Match the serializer's decimal string while preserving a valid zero rate.
    risk_free_rate:
      Number(m.allocation_method) === Number(OPM) && riskFreeRate !== undefined ? String(riskFreeRate) : undefined,
    // The OPM editor owns the pricing date; saved methodology columns can lag behind it.
    opm_backsolve_date: Number(m.allocation_method) === Number(OPM) ? backsolveDate : m.opm_backsolve_date,
  }));

  return methodologies as (ColumnWithWeightProps & Pick<AppliedMethodologySerializer, 'risk_free_rate'>)[];
};

const getOPMValuesFromCells = (opmData: any) => {
  const { maturity: mCell, volatility: vCell, risk_free_rate: rateCell, opm_backsolve_date } = keyBy(opmData, 'alias');
  const maturity = Number(mCell?.value ?? 0);
  // Guard empty inputs: dbShortDate defaults an absent date to today.
  const dateValue = opm_backsolve_date?.value;
  const backsolveDate = typeof dateValue === 'string' && dateValue ? dbShortDate(dateValue) : null;
  const volatility = Number(vCell?.value ?? 0);
  const riskFreeRate = rateCell?.value == null || rateCell.value === '' ? undefined : Number(rateCell.value);
  return { maturity, volatility, riskFreeRate, backsolveDate };
};

const mapValuesWithMethodology = (values: any, index: number, methodology: any) =>
  values?.map((v: any) => ({ ...v, col: index + 1, allocationMethod: methodology.allocation_method })) ?? [];

const buildScenarioData = ({
  appliedMethodologies,
  currentNonZeroRows,
  securityList,
  opmValues,
  targetValue,
  allocation,
}: ScenarioDataInput) => {
  const { maturity, volatility, riskFreeRate, backsolveDate } = opmValues;

  return {
    applied_methodologies: populateAppliedMethodologies(
      appliedMethodologies,
      maturity,
      volatility,
      riskFreeRate,
      backsolveDate
    ),
    target_value: Number(targetValue),
    basket: populateBasket(currentNonZeroRows, securityList),
    allocation,
  };
};

const hasCalculationInputChanged = ({
  opmValues,
  prevOpmData,
  editableLedgerCells,
  prevEditableLedgerCells,
  targetValue,
  lastSolvedTarget,
}: CalculationInputChange) => {
  const {
    maturity: prevMaturity,
    volatility: prevVolatility,
    risk_free_rate: prevRiskFreeRate,
  } = keyBy(prevOpmData, 'alias');
  const { backsolveDate: previousBacksolveDate } = getOPMValuesFromCells(prevOpmData);
  const { maturity, volatility, riskFreeRate, backsolveDate } = opmValues;

  // lastSolvedTarget is unset until a solve completes or is skipped, so a fresh mount defers to
  // the other signals rather than treating its starting target as an edit.
  const targetValueChanged = lastSolvedTarget !== null && toString(targetValue) !== lastSolvedTarget;

  return (
    !isEqual(editableLedgerCells, prevEditableLedgerCells)
    || targetValueChanged
    || toString(maturity) !== toString(prevMaturity?.value)
    || toString(volatility) !== toString(prevVolatility?.value)
    || toString(riskFreeRate) !== toString(prevRiskFreeRate?.value)
    // The pricing date affects accrued dividends even when FRED returns the same rate.
    || backsolveDate !== previousBacksolveDate
  );
};

const isScenarioDataValid = ({ data, rows, targetValue, allocation, riskFreeRate }: ScenarioDataValidity) => {
  const hasEveryBasketRow = data.basket.length === rows.length;
  const isValidWeight = validateWeighting({ columnsWithWeights: data.applied_methodologies });
  // every() is vacuously true without OPM methodologies, which is the non-OPM case.
  const hasCompleteOpmInputs = data.applied_methodologies
    .filter(am => Number(am.allocation_method) === Number(OPM))
    .every(
      am =>
        am.maturity && am.maturity > 0 && am.volatility && am.volatility > 0 && am.opm_backsolve_date
        && Number.isFinite(riskFreeRate)
    );

  return Boolean(hasEveryBasketRow && targetValue && allocation && isValidWeight && hasCompleteOpmInputs);
};

const executeBacksolveCalculation = async ({
  data,
  exitDate,
  backsolveDate,
  scenarioType,
  targetValue,
  prevAllocationParams,
  spreadsheets,
  onCellsChanged,
  isTargetValueExplicitlySet,
  isCurrentCalculation,
  fetchScenarioEquityValue,
  updateScenarioValues,
  setPrevAllocationParams,
  setScenarioEquityValue,
  lastSolvedTargetValue,
  processErrorResponse,
}: ExecuteBacksolveCalculationParams) => {
  try {
    const equityValue = await fetchScenarioEquityValue(data);
    if (!isCurrentCalculation()) {
      return {};
    }

    const allocationParams: UpdateScenarioValuesProps = {
      backsolveData: data,
      exitDate,
      marketDate: backsolveDate ?? null,
      equityValue,
      scenarioType,
    };

    let newDistribution: unknown[] = [];
    if (!isEqual(allocationParams, prevAllocationParams)) {
      newDistribution = await updateScenarioValues(allocationParams);
      if (!isCurrentCalculation()) {
        return {};
      }
      setPrevAllocationParams(allocationParams);
      checkEmptyRows(newDistribution, spreadsheets, onCellsChanged, data, isTargetValueExplicitlySet);
    }

    setScenarioEquityValue(equityValue);
    changeIfBacksolveSummarySheet(spreadsheets, onCellsChanged, equityValue);
    lastSolvedTargetValue.current = toString(targetValue);

    return { equityValue, newDistribution };
  } catch (error: unknown) {
    if (!isCurrentCalculation()) {
      return {};
    }

    const errorMessage = extractServerErrorMessage(error) ?? extractResponseErrorMessage(error);
    processErrorResponse({
      error,
      defaultErrorMessage: errorMessage ?? undefined,
      action: 'get scenario equity value',
    });

    return {};
  }
};

const useCalculateEquityValue = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [scenarioEquityValue, setScenarioEquityValue] = useState<number>(0);
  const [prevAllocationParams, setPrevAllocationParams] = useState<any>({});
  const latestCalculation = useRef(0);
  // The target value the most recent successful solve was run for. A failed solve leaves it
  // untouched so the next attempt retries instead of being skipped as "unchanged".
  const lastSolvedTargetValue = useRef<string | null>(null);
  const { processErrorResponse } = useResponse();

  // A response from a departed approach must not publish into its former workbook.
  useEffect(() => () => {
    latestCalculation.current += 1;
  }, []);

  const fetchScenarioEquityValue = useCallback(async (data: MultiAllocationInputSerializer) => {
    const response = await AllocationsService.apiAllocationsCalculateBacksolveCreate(data);

    if (response) {
      const { equity_value: equityValue } = response;
      return equityValue;
    }

    return 0;
  }, []);

  const updateScenarioValues = async ({
    backsolveData,
    exitDate,
    equityValue,
    scenarioType,
    marketDate,
  }: UpdateScenarioValuesProps) => {
    const { applied_methodologies: methodologies } = backsolveData;

    const scenarioValues = await Promise.all(
      methodologies.map(async (m: any, index: number) => {
        const response = await ApiService.apiAllocationScenarioAllocationResultsCreate({
          scenarioType,
          exitDate,
          equityValue: toString(equityValue),
          captableId: m.cap_table,
          scenarioMethod: m.allocation_method,
          maturity: toString(m.maturity),
          volatility: toString(m.volatility),
          riskFreeRate: m.risk_free_rate,
          marketDate,
        });

        if (!response) {
          return {};
        }

        const {
          present_values: presentValues,
          aggregate_values: aggregateValues,
          future_values: futureValues,
        } = response;

        return {
          presentValues: mapValuesWithMethodology(presentValues, index, m),
          aggregateValues: mapValuesWithMethodology(aggregateValues, index, m),
          futureValues: mapValuesWithMethodology(futureValues, index, m),
        };
      })
    );

    return scenarioValues;
  };

  const calculateEquityValue = useCallback(
    async ({
      scenarioType,
      allocation,
      appliedMethodologies,
      // opmData is the opmTable cells
      opmData,
      securityRows,
      targetValue,
      prevOpmData,
      editableLedgerCells,
      prevEditableLedgerCells,
      securityList,
      spreadsheets,
      onCellsChanged,
      isTargetValueExplicitlySet,
    }: CalculateEquityValueProps) => {
      // Equity and distribution are one operation; newer inputs supersede both requests.
      const calculation = ++latestCalculation.current;
      const isCurrentCalculation = () => calculation === latestCalculation.current;
      const exitDate = currentISODate();
      const rows = securityRows;
      const opmValues = getOPMValuesFromCells(opmData);
      const { riskFreeRate } = opmValues;

      const currentNonZeroRows = securityRows.filter(row => Number(row.shares) > 0);

      const dataChanged = hasCalculationInputChanged({
        opmValues,
        prevOpmData,
        editableLedgerCells,
        prevEditableLedgerCells,
        targetValue,
        lastSolvedTarget: lastSolvedTargetValue.current,
      });

      const data = buildScenarioData({
        appliedMethodologies,
        currentNonZeroRows,
        securityList,
        opmValues,
        targetValue,
        allocation,
      });

      const backsolveDate = data.applied_methodologies.find(am => Number(am.allocation_method) === Number(OPM))?.opm_backsolve_date;
      const dataIsValid = isScenarioDataValid({ data, rows, targetValue, allocation, riskFreeRate });

      if (dataChanged && dataIsValid) {
        setIsLoading(true);
        try {
          return await executeBacksolveCalculation({
            data,
            exitDate,
            backsolveDate,
            scenarioType,
            targetValue,
            prevAllocationParams,
            spreadsheets,
            onCellsChanged,
            isTargetValueExplicitlySet,
            isCurrentCalculation,
            fetchScenarioEquityValue,
            updateScenarioValues,
            setPrevAllocationParams,
            setScenarioEquityValue,
            lastSolvedTargetValue,
            processErrorResponse,
          });
        } finally {
          if (isCurrentCalculation()) {
            setIsLoading(false);
          }
        }
      }
      // Nothing to solve. Record the current target, so the first edit to it afterwards
      // is recognized as a change rather than compared against nothing.
      lastSolvedTargetValue.current ??= toString(targetValue);
      setIsLoading(false);
      return {};
    },
    [fetchScenarioEquityValue, prevAllocationParams, processErrorResponse]
  );

  return {
    calculateEquityValue,
    isLoading,
    scenarioEquityValue,
  };
};

export default useCalculateEquityValue;
